import React, { useEffect, useState, Component } from 'react';
import { useUser, UserProfile } from '@clerk/clerk-react';
import { useAppStore, useWebSocketStore, useNotificationStore } from './store';
import * as api from './utils/api';
import { NotificationToast } from './components/NotificationToast';
import { registerServiceWorker } from './utils/pushNotifications';

// Components
import AuthPage from './components/AuthPage';

import Header from './components/Header';
import Sidebar from './components/Sidebar';
import TabNavigation from './components/TabNavigation';
import StructureAnalysis from './components/StructureAnalysis';
import ConfluenceScore from './components/ConfluenceScore';
import AIChecklist from './components/AIChecklistNew';  // NEW ADVANCED CHECKLIST
// import TradingJournal from './components/TradingJournal';  // REMOVED
import RightSidebar from './components/RightSidebar';
import MobileSidebar from './components/MobileSidebar';
import SessionScanner from './components/SessionScanner';
import AIDeepAnalysis from './components/AIDeepAnalysis';
import ProfilePage from './components/ProfilePage';
import LotSizeCalculator from './components/LotSizeCalculator';
import StrategiesPage from './components/StrategiesPage';

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
          <div className="glass-card p-6 max-w-md text-center">
            <h2 className="text-xl font-bold text-red-400 mb-4">Qualcosa è andato storto</h2>
            <p className="text-slate-400 mb-4">Errore tecnico: {this.state.error?.message}</p>
            <pre className="text-xs text-left bg-slate-900 p-2 rounded mb-4 overflow-auto max-h-40">
              {this.state.error?.stack}
            </pre>
            <p className="text-slate-400 mb-4">Si è verificato un errore. Prova a ricaricare la pagina.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600"
            >
              Ricarica Pagina
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  // ADMIN BYPASS MODE - Set to true to skip auth
  const ADMIN_MODE = false;  // ← Auth ENABLED - Users must login via Clerk

  // Use Clerk authentication
  const { isSignedIn, isLoaded, user } = useUser();

  const {
    activeTab,
    selectedPair,
    setMt5Connected,
    setAccountInfo,
    setPrices,
    setStructureAnalysis,
    setConfluenceData,
    setJournalStats,
    setCalendarData,
    setWeeklySummary,
    setLoading,
    setError,
  } = useAppStore();

  const { connect: connectWebSocket } = useWebSocketStore();
  const { notifications } = useNotificationStore();
  const [appError, setAppError] = useState(null);
  const [activeToast, setActiveToast] = useState(null);
  const [shownToastIds, setShownToastIds] = useState(new Set());



  // Initial data fetch with robust error handling
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Get status with timeout
        const statusPromise = api.getStatus();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 10000)
        );

        const statusRes = await Promise.race([statusPromise, timeoutPromise]).catch(() => ({ data: { mt5_connected: false } }));
        setMt5Connected(statusRes.data?.mt5_connected || false);

        // Get account info (don't fail if this errors)
        try {
          const accountRes = await api.getAccount();
          if (accountRes?.data) setAccountInfo(accountRes.data);
        } catch (e) {
          console.log('Account fetch skipped:', e.message);
        }

        // Get prices (don't fail if this errors)
        try {
          setLoading('prices', true);
          const pricesRes = await api.getPrices();
          if (pricesRes?.data && Array.isArray(pricesRes.data)) {
            setPrices(pricesRes.data);
          }
        } catch (e) {
          console.log('Prices fetch skipped:', e.message);
        } finally {
          setLoading('prices', false);
        }

        // Get journal stats
        try {
          const statsRes = await api.getJournalStats();
          if (statsRes?.data) setJournalStats(statsRes.data);
        } catch (e) {
          console.log('Journal not available yet');
        }

      } catch (error) {
        console.error('Error fetching initial data:', error);
        // Don't set error to avoid blocking UI
      }
    };

    fetchInitialData();

    // Connect WebSocket
    connectWebSocket();

    // Register Service Worker for push notifications (non-blocking)
    registerServiceWorker().catch(err => {
      console.log('Service Worker registration skipped:', err.message);
    });
  }, []);

  // Gestione toast per nuove notifiche
  useEffect(() => {
    // Prendi l'ultima notifica non letta che non è ancora stata mostrata
    const unreadNotifications = notifications.filter(n => !n.read && !shownToastIds.has(n.id));
    
    if (unreadNotifications.length > 0) {
      const latest = unreadNotifications[0];
      // Mostra toast solo se è diverso da quello attualmente mostrato
      if (!activeToast || activeToast.id !== latest.id) {
        setActiveToast(latest);
        // Segna come mostrato
        setShownToastIds(prev => new Set([...prev, latest.id]));
      }
    }
    
    // Se il toast attivo è stato letto o chiuso, rimuovilo
    if (activeToast) {
      const toastNotification = notifications.find(n => n.id === activeToast.id);
      if (!toastNotification || toastNotification.read) {
        setActiveToast(null);
      }
    }
    
    // Se non ci sono più notifiche non lette da mostrare, chiudi il toast
    if (unreadNotifications.length === 0 && activeToast) {
      setActiveToast(null);
    }
  }, [notifications, activeToast, shownToastIds]);

  // Fetch analysis when selected pair changes
  useEffect(() => {
    if (!selectedPair) return;

    let isMounted = true;
    const controller = new AbortController();

    const fetchAnalysis = async () => {
      setLoading('analysis', true);

      try {
        // Fetch with timeout
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 15000)
        );

        const [structureRes, confluenceRes] = await Promise.race([
          Promise.all([
            api.analyzeStructure(selectedPair).catch(() => ({ data: { analysis: {} } })),
            api.getConfluence(selectedPair).catch(() => ({ data: { confluence: {} } })),
          ]),
          timeout
        ]);

        if (isMounted) {
          if (structureRes?.data?.analysis) {
            setStructureAnalysis(selectedPair, structureRes.data.analysis);
          }
          if (confluenceRes?.data?.confluence) {
            setConfluenceData(selectedPair, confluenceRes.data.confluence);
          }
        }

      } catch (error) {
        console.error('Analysis fetch error:', error.message);
        // Don't set error to avoid blocking UI
      } finally {
        if (isMounted) setLoading('analysis', false);
      }
    };

    fetchAnalysis();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [selectedPair]);

  // Fetch journal data when tab changes to journal
  useEffect(() => {
    if (activeTab === 'journal') {
      const fetchJournalData = async () => {
        setLoading('journal', true);

        try {
          const now = new Date();
          const [statsRes, calendarRes, weeklyRes] = await Promise.all([
            api.getJournalStats(),
            api.getCalendar(now.getFullYear(), now.getMonth() + 1),
            api.getWeeklySummary(),
          ]);

          setJournalStats(statsRes.data);
          setCalendarData(calendarRes.data);
          setWeeklySummary(weeklyRes.data);

        } catch (error) {
          console.error('Error fetching journal data:', error);
        } finally {
          setLoading('journal', false);
        }
      };

      fetchJournalData();
    }
  }, [activeTab]);

  // Show loading state while Clerk initializes
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth page if not signed in
  if (!isSignedIn) {
    return <AuthPage />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <Header />

      {/* Mobile Sidebar */}
      <MobileSidebar />

      {/* Toast Notification */}
      {activeToast && (
        <NotificationToast
          notification={activeToast}
          onClose={() => {
            setActiveToast(null);
            // Segna come mostrato quando viene chiuso manualmente
            setShownToastIds(prev => new Set([...prev, activeToast.id]));
          }}
        />
      )}

      {/* Main Content */}
      <div className="relative max-w-[1800px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <div className="flex gap-4 sm:gap-6">
          {/* Left Sidebar - Watchlist */}
          <Sidebar />

          {/* Main Content Area */}
          <div className="flex-1 space-y-4 sm:space-y-6 min-w-0">
            {/* Tab Navigation */}
            <TabNavigation />

            {/* Tab Content */}
            {activeTab === 'scanner' && (
              <div className="animate-fadeIn">
                <SessionScanner />
              </div>
            )}

            {/* Structure Analysis tab removed */}
            {/*
            {activeTab === 'analysis' && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 animate-fadeIn">
                <StructureAnalysis />
                <ConfluenceScore />
              </div>
            )}
            */}

            {activeTab === 'checklist' && (
              <div className="animate-fadeIn">
                <AIChecklist />
              </div>
            )}

            {activeTab === 'strategies' && (
              <div className="animate-fadeIn">
                <StrategiesPage />
              </div>
            )}

            {/* Journal tab removed */}

            {activeTab === 'calculator' && (
              <div className="animate-fadeIn">
                <LotSizeCalculator />
              </div>
            )}

            {activeTab === 'ai-analysis' && (
              <div className="animate-fadeIn">
                <AIDeepAnalysis />
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="animate-fadeIn">
                <ProfilePage />
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <RightSidebar />
        </div>
      </div>
    </div>
  );
}

// Wrap with Error Boundary
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
